import { defineStore } from 'pinia'
import storage, { SIDEBAR_STATUS, TOKEN_INFO } from '@shared/utils/storage'
import { getUserInfo } from '@shared/apis/user'
import { getPrefix } from '@shared/apis/oss'
import routes from '@/router/permissionRoutes'
import { cloneDeep, intersection } from 'lodash-es'
import { getAreaList } from '../apis/gis'

import { configGet } from '@shared/apis/sysConfig'
import {
  getAreaScopeDetail,
  getEnvironmentScope,
  getEnvironmentScopeDetail,
  getIotScopeDetail,
  getPdScope,
  getPdScopeDetail,
  getWaterLoggingScope,
  getWaterLoggingScopeDetail,
  getAreaScopeTreeList
} from '../apis/area'
export default defineStore({
  id: 'useAppStore',
  state: () => ({
    router: null, // vue-router实例
    /** 菜单栏收缩状态 */
    sidebar: {
      opened: storage.getItem(SIDEBAR_STATUS) || true
    },
    /** 用户信息 */
    userInfo: null,
    /** 权限列表 */
    allPermissionStr: '',
    /** 筛选过后有权限的路由 */
    permissionRoutes: [],
    /** 当前激活的系统(navbar) */
    activeSystem: '',
    /** 动态系统列表，(navbar) */
    dynamicSystemList: new Map(),
    /** 是否显示(navbar) */
    showNavbar: true,
    /** 文件url */
    ossBaseUrl: null,
    // 项目配置
    projectConfig: {},
    areaConfig: null
  }),
  getters: {
    activeSystemItem(state) {
      return state.permissionRoutes.find((i) => i.name === state.activeSystem)
    }
  },
  actions: {
    async getOssUrl() {
      return new Promise((resolve) => {
        if (this.ossBaseUrl) {
          resolve(this.ossBaseUrl)
        } else {
          getPrefix().then((res) => {
            this.ossBaseUrl = res + '/'
            resolve(this.ossBaseUrl)
          })
        }
      })
    },
    /** 拼接ossUrl */
    joinOssUrl(url) {
      return (this.ossBaseUrl + url).replace(/(?<!:)\/\/+/g, '/')
    },
    toggleSide() {
      this.sidebar.opened = !this.sidebar.opened
    },
    isAuth(str) {
      return this.allPermissionStr.includes(str)
    },
    isRole(roleKeys = []) {
      return !!intersection(roleKeys, this.userInfo.roleKeys)?.length
    },
    async setUserInfo(refresh) {
      // refresh 刷新getUserInfo接口
      return new Promise((resolve) => {
        if (this.userInfo && !refresh) {
          resolve(this.userInfo)
        } else {
          getUserInfo().then((data) => {
            this.userInfo = data
            // 处理权限路由
            this.allPermissionStr = data.perms

            // 筛选有权限的路由菜单
            this.filterPermissionRoutes()
            resolve(this.userInfo)
          })
        }
      })
    },
    async getAreaDatas() {
      return getAreaList().then((areaList) => {
        this.areaList = areaList
        this.areaMap = areaList.reduce((pre, cur) => {
          pre[cur.areaId] = cur
          return pre
        }, {})
      })
    },
    // 筛选有权限的路由菜单
    filterPermissionRoutes() {
      /** 是否添加了默认路由 */
      let isAddDefaultRoute = false

      // 判断路由有无展示权限
      const isAuth = (route) =>
        this.isAuth(route.meta?.permissionStr) || this.isRole(route.meta.permissionRoles) || route.meta?.alwaysShow

      // 只有单个系统的时候不显示顶部菜单
      this.showNavbar = routes.length !== 1

      this.permissionRoutes = cloneDeep(routes).filter((sys) => {
        sys.routes = sys.routes?.filter((routeItem) => {
          // 递归判断每个路由下的子项路由有没有权限，并且设置第一个有页面的路由为默认重定向路由
          const recursion = (route) => {
            route.children = route.children
              .filter((r) => isAuth(r))
              .map((r) => {
                // 路由存下菜单名字，好做activeSystem
                r.meta.systemName = sys.name

                if (!isAddDefaultRoute && r.component) {
                  isAddDefaultRoute = true
                  // 添加默认重定向的路由
                  this.router.addRoute({
                    path: '/',
                    redirect: r.path
                  })
                }

                if (r.children) recursion(r)
                return r
              })
          }
          recursion(routeItem)

          // 过滤掉没有子项的路由
          if (routeItem.children.length || routeItem?.meta?.special) this.router.addRoute(routeItem)
          return routeItem.children.length
        })

        return sys.routes.length
      })
    },

    //地图配置不同系统调用不同接口
    async getMapConfig() {
      let key = import.meta.env.VITE_SYSTEM_ID || 5
      const api = {
        3: {
          tree: getAreaScopeTreeList,
          detail: getAreaScopeDetail
        },
        5: {
          tree: getEnvironmentScope,
          detail: getEnvironmentScopeDetail
        },
        6: {
          tree: getPdScope,
          detail: getPdScopeDetail
        },
        7: {
          tree: getWaterLoggingScope,
          detail: getWaterLoggingScopeDetail
        },
        8: {
          tree: getAreaScopeTreeList,
          detail: getAreaScopeDetail
        }
      }
      if (key == 4) {
        return getIotScopeDetail().then((res) => {
          this.areaConfig = res
        })
      } else {
        return new Promise((resolve) => {
          if (this.areaConfig) {
            resolve(this.areaConfig)
          } else {
            api[key].tree().then((res) => {
              if (res) {
                this.areaTree = res
                api[key]
                  .detail({
                    areaId: res.areaId
                  })
                  .then((areaConfig) => {
                    this.areaConfig = areaConfig
                    resolve(this.areaConfig)
                  })
              } else {
                resolve()
              }
            })
          }
        })
      }

      console.log(import.meta.env.VITE_SYSTEM_ID, 'import.meta.env.VITE_SYSTEM_ID')
    },
    /** 获取系统配置 */
    async getProjectConfig() {
      return configGet({ id: 2 })
        .then((res) => {
          try {
            JSON.parse(res.configParameter)
          } catch (error) {
            this.projectConfig = {}
            return
          }
          this.projectConfig = JSON.parse(res.configParameter) || {}
        })
        .catch(() => {
          console.error('接口不存在走本地配置')
        })
    },

    /** 加载系统需要提前获取的接口，统一在这请求 */
    initData(path) {
      const store = useStore()
      const list = [this.getOssUrl(), this.getMapConfig()]
      if (store.initData) list.push(store.initData(path))

      return Promise.allSettled(list)
    }
  }
})
